import { faCheckCircle, faTimesCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as AuctionTooltip from 'components/pages/campaign-settings/campaign-auction/AuctionTooltip';
import * as Tooltip from 'components/tooltip';
import { capitalize } from 'lodash';
import { Bid } from 'types/domain';

interface BidStatusProps {
    status: Bid['status'];
    className?: string;
}

interface BidActionPillProps {
    status: Bid['dashboard_status'];
    className?: string;
}

interface BidItemStatusPillProps {
    status: Bid['status'];
    className?: string;
}

const getBidSubStatus = (status: BidStatusProps['status']) => {
    switch (status) {
        case 'uncollectable':
            return 'Uncollectable';
        case 'winner_paid':
        case 'buy_now':
        case 'second_chance_paid':
            return 'Paid';
        default:
            return 'Pending';
    }
};

const getItemBidStatus = (status: BidStatusProps['status']) => {
    switch (status) {
        case 'uncollectable':
            return 'Uncollectable';
        case 'winner_paid':
        case 'buy_now':
        case 'second_chance_paid':
            return 'Paid';
        case 'buy_now_unpaid':
            return 'Awaiting Payment';
        case 'top_bidder':
            return 'Top Bidder';
        case 'disqualified':
            return 'Disqualified';
        case 'outbid':
            return 'Outbid';
        case 'second_chance':
            return 'Offer Sent';
        case 'second_chance_expired':
            return 'Offer Expired';
        default:
            return capitalize(status);
    }
};

const BidStatusContainer = (props: BidStatusProps) => {
    const { status, className: classNameConsumer = '' } = props;
    let className = `${classNameConsumer} `;

    switch (status) {
        case 'winner_paid':
        case 'buy_now':
        case 'second_chance_paid':
            className += 'text-green-500 bg-green-100';
            break;
        case 'uncollectable':
            className += 'text-red-500 bg-red-100';
            break;
        default:
            className += 'bg-orange-100 text-orange-500';
    }

    return <p className={className}>{getBidSubStatus(status)}</p>;
};

const BidActionPill = (props: BidActionPillProps) => {
    const { status, className: classNameConsumer = '' } = props;
    let className = `${classNameConsumer} `;

    switch (status) {
        case 'Complete':
            className += 'text-green-500 bg-green-100';
            break;
        case 'Failed Payment':
        case 'Failing Payment':
        case 'Second Chance Expired':
            className += 'text-red-500 bg-red-100';
            break;
        case 'Offer Sent':
        case 'Awaiting Payment':
            className += 'text-blue-500 bg-blue-100';
            break;
        case 'Pending Fulfillment':
        default:
            className += 'text-black bg-gray-100';
    }

    return <p className={className}>{status}</p>;
};

const BidItemStatusPill = (props: BidItemStatusPillProps) => {
    const { status, className: classNameConsumer = '' } = props;
    let className = `${classNameConsumer} `;

    switch (status) {
        case 'winner':
        case 'winner_paid':
        case 'buy_now':
        case 'second_chance_paid':
        case 'top_bidder':
            className += 'text-green-700 bg-green-100';
            break;
        case 'disqualified':
            className += 'text-red-700 bg-red-100';
            break;
        case 'outbid':
            className += 'text-yellow-700 bg-yellow-100';
            break;
        case 'buy_now_unpaid':
            className += 'text-blue-700 bg-blue-100';
            break;
        default:
            className += 'text-black bg-gray-100';
    }

    return <p className={className}>{getItemBidStatus(status)}</p>;
};

const BidStatusTooltip = (props: BidStatusProps) => {
    const { status, className: classNameConsumer = '' } = props;
    let className = `${classNameConsumer} `;
    let Icon = faCheckCircle;

    switch (status) {
        case 'winner_paid':
        case 'buy_now':
        case 'second_chance_paid':
            className += 'text-green-500';
            break;
        case 'uncollectable':
            className += 'text-red-500';
            Icon = faTimesCircle;
            break;
        default:
            className += 'text-orange-500';
    }

    return (
        <AuctionTooltip.Root>
            <Tooltip.Trigger asChild={true} tabIndex={0} className={className}>
                <FontAwesomeIcon icon={Icon} />
            </Tooltip.Trigger>
            <AuctionTooltip.Content>{getBidSubStatus(status)}</AuctionTooltip.Content>
        </AuctionTooltip.Root>
    );
};

export { getBidSubStatus, BidStatusContainer, BidStatusTooltip, BidActionPill, BidItemStatusPill };
